import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, MoreVertical, Circle, Link as LinkIcon, CheckSquare, FileText, Zap, File, Database, Network, Layout, Wand2, Box, PenTool, LayoutDashboard, Lock, Columns, List, ListOrdered, Folder, Code, ChevronDown, Table, LayoutTemplate, Slash } from 'lucide-react';
import { Youtube } from '../../../../../shared/ui/icons/BrandIcons';
import { docsApi } from '../../../api';
import './lilmenu.css';

// Integración de los avances de main (ver .claude/plans/tech-debt): rediseño de links como
// "capsulas" con color + texto editable + auto-fetch del <title> real de la página (antes: un
// `prompt()` simple que solo pedía la URL).
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: deriva el formato activo de la selección del DOM y precarga los campos del editor de link al pasar sobre uno existente.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const LINK_COLORS = [
    { name: 'Verde', value: '#4CAF50' },
    { name: 'Azul', value: '#42A5F5' },
    { name: 'Amarillo', value: '#FFC107' },
    { name: 'Rojo', value: '#EF5350' },
    { name: 'Blanco', value: '#e0e0e0' },
    { name: 'Morado', value: '#AB47BC' },
];


const rgbToHex = (rgb) => {
    const rgbMatch = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (!rgbMatch) return rgb; // could be hex already or named color
    const hex = (x) => ("0" + parseInt(x).toString(16)).slice(-2);
    return "#" + hex(rgbMatch[1]) + hex(rgbMatch[2]) + hex(rgbMatch[3]);
};

const TEXT_COLORS = ['#EF5350','#EC407A','#AB47BC','#7E57C2','#5C6BC0','#42A5F5','#29B6F6','#26C6DA','#26A69A','#66BB6A','#FFA726','#8D6E63','#757575','#BDBDBD'];

const cleanUrl = (url) => {
    if (!url) return '';
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

const truncate = (str, max) => {
    if (!str) return '';
    return str.length > max ? str.substring(0, max) + '…' : str;
};

const LilMenu = ({ position, isOpen, onSelect, onClose, linkContext, mode = 'full' }) => {
    const [activeDropdown, setActiveDropdown] = useState(null); // 'text', 'styles' o 'link'
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    const [linkColor, setLinkColor] = useState('#4CAF50');
    const [isFetchingTitle, setIsFetchingTitle] = useState(false);
    const menuRef = useRef(null);
    const urlInputRef = useRef(null);
    const [activeFormat, setActiveFormat] = useState(null);
    const [activeColors, setActiveColors] = useState([]);
    const [activeMarks, setActiveMarks] = useState(new Set());
    const [slashQuery, setSlashQuery] = useState('');
    const [slashIndex, setSlashIndex] = useState(0);
    const slashInputRef = useRef(null);

    const SLASH_OPTIONS = [
        { id: 'tarea', label: 'Tarea', action: 'convert', value: 'tarea', icon: CheckSquare },
        { id: 'documento', label: 'Documento', action: 'convert', value: 'documento', icon: FileText },
        { id: 'inline-page', label: 'Inline página', action: 'insert-block', value: 'inline-page', icon: FileText },
        { id: 'db-inline', label: 'Inline database', action: 'insert-block', value: 'db-inline', icon: Database },
        { id: 'inline-flow', label: 'Inline flow', action: 'insert-block', value: 'inline-flow', icon: Network },
        { id: 'inline-frame', label: 'Inline frame', action: 'insert-block', value: 'inline-frame', icon: Layout },
        { id: 'inline-wizard', label: 'Inline wizard', action: 'insert-block', value: 'inline-wizard', icon: Wand2 },
        { id: 'kanban', label: 'Inline kanban', action: 'insert-block', value: 'kanban', icon: LayoutDashboard },
        { id: 'inline-social-outline', label: 'Inline task social outline', action: 'insert-block', value: 'inline-social-outline', icon: List },
        { type: 'divider' },
        { id: 'password', label: 'Proteger con contraseña', action: 'insert-block', value: 'password', icon: Lock },
        { id: 'docu-extract', label: 'Docu extracto', action: 'insert-block', value: 'docu-extract', icon: FileText },
        { id: 'task-extract', label: 'Task extracto', action: 'insert-block', value: 'task-extract', icon: CheckSquare },
        { type: 'divider' },
        { id: 'onepage', label: 'Onepage', action: 'insert-html', value: 'onepage', icon: LayoutTemplate }
    ];

    const filteredSlashOptions = SLASH_OPTIONS.filter(opt => 
        opt.type === 'divider' || opt.label.toLowerCase().includes(slashQuery.toLowerCase())
    ).filter((opt, i, arr) => {
        // Remove trailing or adjacent dividers
        if (opt.type === 'divider') {
            if (i === 0) return false;
            if (i === arr.length - 1) return false;
            if (arr[i-1].type === 'divider') return false;
        }
        return true;
    });

    const selectableSlashOptions = filteredSlashOptions.filter(o => o.type !== 'divider');

    useEffect(() => {
        if (activeDropdown === 'slash') {
            setSlashQuery('');
            setSlashIndex(0);
            setTimeout(() => slashInputRef.current?.focus(), 50);
        }
    }, [activeDropdown]);

    const handleSlashKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSlashIndex(prev => Math.min(prev + 1, selectableSlashOptions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSlashIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectableSlashOptions[slashIndex]) {
                const opt = selectableSlashOptions[slashIndex];
                handleAction(e, opt.action, opt.value);
            }
        }
    };


    // Detect format on open or dropdown toggle
    useEffect(() => {
        if (isOpen && (activeDropdown === 'text' || activeDropdown === 'styles' || activeDropdown === 'color' || !activeDropdown)) {
            const sel = window.getSelection();
            if (sel.rangeCount > 0) {
                let node = sel.anchorNode;
                if (node && node.nodeType === 3) node = node.parentNode;
                
                let format = '16px'; // default
                let current = node;
                let foundColors = new Set();
                
                let foundMarks = new Set();
                
                // Extract color and marks from the exact selection contents if possible
                try {
                    const range = sel.getRangeAt(0);
                    const fragment = range.cloneContents();
                    const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT);
                    let el;
                    while ((el = walker.nextNode())) {
                        if (el.style && el.style.color) foundColors.add(el.style.color);
                        const t = el.tagName?.toLowerCase();
                        if (t === 'strong' || t === 'b' || (el.style && el.style.fontWeight === 'bold')) foundMarks.add('bold');
                        if (t === 'em' || t === 'i' || (el.style && el.style.fontStyle === 'italic')) foundMarks.add('italic');
                        if (t === 'u' || (el.style && el.style.textDecoration === 'underline')) foundMarks.add('underline');
                        if (t === 's' || t === 'strike' || (el.style && el.style.textDecoration === 'line-through')) foundMarks.add('strikeThrough');
                        if (t === 'blockquote') foundMarks.add('quote');
                    }
                } catch(e) {}

                while (current && current.contentEditable !== 'true') {
                    if (current.style && current.style.color) foundColors.add(current.style.color);
                    
                    const tag = current.tagName?.toLowerCase();
                    if (tag === 'strong' || tag === 'b' || (current.style && current.style.fontWeight === 'bold')) foundMarks.add('bold');
                    if (tag === 'em' || tag === 'i' || (current.style && current.style.fontStyle === 'italic')) foundMarks.add('italic');
                    if (tag === 'u' || (current.style && current.style.textDecoration === 'underline')) foundMarks.add('underline');
                    if (tag === 's' || tag === 'strike' || (current.style && current.style.textDecoration === 'line-through')) foundMarks.add('strikeThrough');
                    if (tag === 'blockquote') foundMarks.add('quote');

                    if (tag === 'h1' && format === '16px') { format = 'h1'; }
                    if (tag === 'h2' && format === '16px') { format = 'h2'; }
                    if (tag === 'h3' && format === '16px') { format = 'h3'; }
                    if (current.style && current.style.fontSize && format === '16px') {
                        format = current.style.fontSize;
                    }
                    current = current.parentNode;
                }
                setActiveMarks(foundMarks);
                setActiveFormat(format);
                
                // Convert DOM rgb() to hex for comparison if necessary, 
                // but usually Tiptap sets it as hex. 
                // If it's rgb, we can just do a loose check or let the user see it.
                // Actually, browsers convert style.color to rgb(...) automatically!
                setActiveColors(Array.from(foundColors));
            }
        }
    }, [isOpen, activeDropdown]);

    // Cuando `linkContext` cambia (cursor sobre un link existente), precarga sus datos y abre
    // el dropdown de link directo.
    useEffect(() => {
        if (linkContext) {
            setActiveDropdown('link');
            setLinkUrl(linkContext.href || '');
            setLinkText(linkContext.text || '');
            setLinkColor(linkContext.color || '#4CAF50');
        }
    }, [linkContext]);

    useEffect(() => {
        if (activeDropdown === 'link') {
            setTimeout(() => urlInputRef.current?.focus(), 50);
        }
    }, [activeDropdown]);

    // Prevent menu from closing when clicking inside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                if (isOpen) {
                    setActiveDropdown(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const toggleDropdown = (e, menu) => {
        e.preventDefault();
        e.stopPropagation();
        if (menu === 'link' && activeDropdown !== 'link') {
            const sel = window.getSelection();
            setLinkText(sel && !sel.isCollapsed ? sel.toString() : '');
            setLinkColor('#4CAF50');
            setLinkUrl('');
        }
        setActiveDropdown(activeDropdown === menu ? null : menu);
    };

    const handleAction = (e, action, value) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(action, value);
        setActiveDropdown(null);
    };

    const handleLinkApply = async (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (!linkUrl.trim()) return;

        let finalUrl = linkUrl.trim();
        if (!/^https?:\/\//i.test(finalUrl)) finalUrl = 'https://' + finalUrl;

        let finalText = linkText.trim();
        if (!finalText) {
            setIsFetchingTitle(true);
            try {
                const data = await docsApi.getLinkTitle(finalUrl);
                if (data.title) finalText = truncate(data.title, 14);
            } catch (err) {
                console.error('Failed to fetch link title', err);
            }
            setIsFetchingTitle(false);
            if (!finalText) finalText = truncate(cleanUrl(finalUrl), 14);
        }

        onSelect('link', { url: finalUrl, text: finalText, color: linkColor });
        setActiveDropdown(null);
        setLinkUrl('');
        setLinkText('');
    };

    const handleLinkKeyDown = (e) => {
        if (e.key === 'Enter') handleLinkApply(e);
    };

    const style = {
        top: position.top,
        left: position.left,
    };

    return (
        <div className="lilmenu-container" style={style} ref={menuRef} onMouseDown={e => {
            // Prevent default only if we are not clicking an input, so focus is not stolen
            if (e.target.tagName !== 'INPUT') e.preventDefault();
        }}>
            {mode === 'full' && (
                <>
                    <button className="lilmenu-btn" onClick={e => toggleDropdown(e, 'slash')}>
                        <Slash size={14} />
                    </button>
                    <div className="lilmenu-vert-sep"></div>
                    <button className="lilmenu-btn" onClick={e => toggleDropdown(e, 'text')}>
                        Texto <span style={{ fontSize: '10px', marginLeft: 'var(--space-1)' }}>▼</span>
                    </button>
                    <div className="lilmenu-vert-sep"></div>
                </>
            )}

            {mode === 'simple' && (
                <>
                    <button className="lilmenu-btn" onClick={e => toggleDropdown(e, 'text')}>
                        Texto <span style={{ fontSize: '10px', marginLeft: 'var(--space-1)' }}>▼</span>
                    </button>
                    <div className="lilmenu-vert-sep"></div>
                </>
            )}
            
            <button className="lilmenu-btn" onClick={e => toggleDropdown(e, 'styles')}>
                Estilos <span style={{ fontSize: '10px', marginLeft: 'var(--space-1)' }}>▼</span>
            </button>
            <button className="lilmenu-btn" onClick={e => toggleDropdown(e, 'color')} title="Color del texto">
                <Circle size={14} fill={activeColors.length === 1 ? activeColors[0] : "currentColor"} color={activeColors.length === 1 ? activeColors[0] : "currentColor"} />
            </button>
            <div className="lilmenu-vert-sep"></div>

            <button className="lilmenu-btn" onClick={e => handleAction(e, 'list', 'checklist')} title="Checklist">
                <Circle size={14} color="#bae6fd" style={{ opacity: 0.8 }} />
            </button>
            <button className="lilmenu-btn" onClick={e => toggleDropdown(e, 'link')} title="Link" style={{ ...(activeDropdown === 'link' ? { backgroundColor: '#333' } : {}) }}>
                <LinkIcon size={14} color="#888" />
            </button>
            <button className="lilmenu-btn" onClick={e => handleAction(e, 'insert-block', 'code')} title="Código">
                <Code size={14} color="#888" />
            </button>

            {mode === 'full' && (
                <>
                    <div className="lilmenu-vert-sep"></div>
                    <button className="lilmenu-btn" onClick={e => handleAction(e, 'comment')} title="Comentar">
                        <MessageCircle size={14} />
                    </button>
                </>
            )}

            {mode === 'simple' && (
                <>
                    <div className="lilmenu-vert-sep"></div>
                    <button className="lilmenu-btn" onClick={e => handleAction(e, 'convert', 'tarea')} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                        <CheckSquare size={14} color="#888" /> Tarea
                    </button>
                    <button className="lilmenu-btn" onClick={e => handleAction(e, 'convert', 'documento')} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                        <FileText size={14} color="#888" /> Documento
                    </button>
                </>
            )}

            {/* SLASH DROPDOWN */}
            {activeDropdown === 'slash' && (
                <div className="lilmenu-dropdown" style={{ minWidth: '220px', maxHeight: '300px', overflowY: 'auto' }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #333' }}>
                        <input
                            ref={slashInputRef}
                            type="text"
                            value={slashQuery}
                            onChange={e => { setSlashQuery(e.target.value); setSlashIndex(0); }}
                            onKeyDown={handleSlashKeyDown}
                            placeholder="Buscar opción..."
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'white', outline: 'none' }}
                        />
                    </div>
                    {filteredSlashOptions.map((opt, idx) => {
                        if (opt.type === 'divider') {
                            return <div key={'div-'+idx} className="lilmenu-separator" style={{ margin: '4px 0' }}></div>;
                        }
                        const isSelected = selectableSlashOptions[slashIndex]?.id === opt.id;
                        const Icon = opt.icon;
                        return (
                            <div 
                                key={opt.id}
                                className="lilmenu-item" 
                                onClick={(e) => handleAction(e, opt.action, opt.value)} 
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
                                }}
                            >
                                <Icon size={14} color="#888" /> {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* TEXT DROPDOWN (Headings, Sizes) */}
            {activeDropdown === 'text' && (
                <div className="lilmenu-dropdown" style={{ minWidth: '120px' }}>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'text', '16px')} style={activeFormat === '16px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>Normal text</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'heading', 1)} style={activeFormat === 'h1' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>Heading 1</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'heading', 2)} style={activeFormat === 'h2' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>Heading 2</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'heading', 3)} style={activeFormat === 'h3' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>Heading 3</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'heading', 4)} style={activeFormat === 'h4' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>Heading 4</div>

                    <div className="lilmenu-separator"></div>

                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '30px')} style={activeFormat === '30px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>30px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '25px')} style={activeFormat === '25px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>25px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '22px')} style={activeFormat === '22px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>22px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '18px')} style={activeFormat === '18px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>18px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '14px')} style={activeFormat === '14px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>14px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '12px')} style={activeFormat === '12px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>12px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '9px')} style={activeFormat === '9px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>9px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '6px')} style={activeFormat === '6px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>6px</div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'size', '4px')} style={activeFormat === '4px' ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>4px</div>
                </div>
            )}

            {/* COLOR DROPDOWN */}
            {activeDropdown === 'color' && (
                <div className="lilmenu-dropdown" style={{ left: 'auto', right: 0 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '4px', padding: '4px 8px' }}>
                        {TEXT_COLORS.map(color => {
                            const isSelected = activeColors.some(ac => rgbToHex(ac).toLowerCase() === color.toLowerCase());
                            return (
                                <div 
                                    key={color}
                                    onClick={(e) => handleAction(e, 'color', color)}
                                    style={{ 
                                        width: '20px', height: '20px', borderRadius: '50%', backgroundColor: color, cursor: 'pointer',
                                        border: isSelected ? '2px solid white' : 'none',
                                        boxShadow: isSelected ? '0 0 4px rgba(255,255,255,0.8)' : 'none'
                                    }}
                                ></div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* STYLES DROPDOWN */}
            {activeDropdown === 'styles' && (
                <div className="lilmenu-dropdown" style={{ left: 'auto', right: 0 }}>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'format', 'bold')} style={activeMarks.has('bold') ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}><b>Bold</b></div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'format', 'italic')} style={activeMarks.has('italic') ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}><i>Italic</i></div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'format', 'underline')} style={activeMarks.has('underline') ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}><u>Underline</u></div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'format', 'strikeThrough')} style={activeMarks.has('strikeThrough') ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}><s>Strike</s></div>
                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'format', 'quote')} style={activeMarks.has('quote') ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}}>Quote</div>

                    <div className="lilmenu-separator"></div>

                    <div className="lilmenu-item" onClick={(e) => handleAction(e, 'format', 'removeFormat')}>Clear Format</div>
                </div>
            )}

            {/* LINK DROPDOWN */}
            {activeDropdown === 'link' && (
                <div className="lilmenu-dropdown lilmenu-link-dropdown" onMouseDown={e => e.stopPropagation()}>
                    <div className="lilmenu-link-colors">
                        {LINK_COLORS.map((c) => (
                            <button
                                key={c.value}
                                className={`lilmenu-color-dot${linkColor === c.value ? ' active' : ''}`}
                                style={{ backgroundColor: c.value }}
                                title={c.name}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLinkColor(c.value); }}
                            />
                        ))}
                    </div>

                    <div className="lilmenu-separator"></div>

                    <div className="lilmenu-link-field">
                        <input
                            type="text"
                            value={linkText}
                            onChange={(e) => setLinkText(e.target.value)}
                            onKeyDown={handleLinkKeyDown}
                            placeholder="Texto del enlace"
                        />
                    </div>

                    <div className="lilmenu-link-field">
                        <input
                            ref={urlInputRef}
                            type="text"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            onKeyDown={handleLinkKeyDown}
                            placeholder="https://ejemplo.com"
                        />
                    </div>

                    <button className="lilmenu-link-apply" onClick={handleLinkApply} disabled={isFetchingTitle}>
                        {isFetchingTitle ? 'Extrayendo...' : 'Aplicar'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default LilMenu;


